import React, { useState, useEffect } from 'react';
import {
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Paper,
  TablePagination,
  Select,
  MenuItem,
  Button,
  Box,
  CircularProgress,
} from '@mui/material';
import Swal from 'sweetalert2';
import { useSucursal } from '../contexts/SucursalContext';
import { useUser } from '../contexts/UserContext';
import { useAccesos } from '../contexts/AccesosContext';

const MovimientosCajero = () => {
  const { tienePermiso } = useAccesos();
  const server = process.env.REACT_APP_API_ROUTE;
  const key = process.env.REACT_APP_API_KEY;
  const [movimientos, setMovimientos] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [tiposTransaccion, setTiposTransaccion] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [editingValue, setEditingValue] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const { cajaAbiertaDatos } = useSucursal();
  const { user } = useUser();
  useEffect(() => {
    const fetchMovimientos = async () => {
      try {
        const response = await fetch(
          `${server}/MgCajaArqueoDetalleSistemas/porIdEncabezado/${cajaAbiertaDatos}`
        );
        const data = await response.json();
        console.log(data);
        setMovimientos(data.$values || data || []);
      } catch (error) {
        console.error('Error al cargar los movimientos:', error);
      }
    };

    if (cajaAbiertaDatos) {
      fetchMovimientos();
    }
  }, [cajaAbiertaDatos]);

  // Cargar tipos de transacción disponibles
  useEffect(() => {
    const fetchTiposTransaccion = async () => {
      try {
        const response = await fetch(
          `${server}/MgCajaTipoTransaccions`
        );
        if (!response.ok) {
          throw new Error(`API error: ${response.status}`);
        }
        const data = await response.json();
        const tiposArray = data.$values || data || [];
        // Filtrar solo los que tienen mostrarEnCierreCaja = true y ordenar por el campo Orden
        const tiposOrdenados = tiposArray
          .filter(tipo => tipo.mostrarEnCierreCaja === true && tipo.descripcionTipoTransaccion)
          .sort((a, b) => (a.orden || 0) - (b.orden || 0));
        setTiposTransaccion(tiposOrdenados);
      } catch (error) {
        console.error('Error al cargar tipos de transacción:', error);
        // Si falla, intentar cargar desde los movimientos mismos
        if (movimientos.length > 0) {
          const tiposUnicos = [...new Map(
            movimientos
              .filter(m => m.idTipoTransaccion && m.descripcionTipoTransaccion)
              .map(m => [m.idTipoTransaccion, { id: m.idTipoTransaccion, descripcionTipoTransaccion: m.descripcionTipoTransaccion }])
          ).values()];
          setTiposTransaccion(tiposUnicos);
        }
      }
    };

    if (server) {
      fetchTiposTransaccion();
    }
  }, [server, movimientos]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleEditarTipo = (movimiento) => {
    setEditingId(movimiento.id);
    setEditingValue(String(movimiento.idTipoTransaccion) || '');
  };

  const handleCancelarEdicion = () => {
    setEditingId(null);
    setEditingValue('');
  };

  const handleGuardarEdicion = async (movimiento) => {
    if (!editingValue) {
      Swal.fire({
        icon: 'warning',
        title: 'Advertencia',
        text: 'Debe seleccionar un tipo de transacción',
      });
      return;
    }

    setIsSaving(true);
    try {
      const response = await fetch(
        `${server}/MgCajaArqueoDetalleSistemas/${movimiento.id}`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'X-API-Key': key,
          },
          body: JSON.stringify({
            ...movimiento,
            idTipoTransaccion: editingValue,
            modificadoPor: user?.codigoUsuario || 'ADMIN               ',
            fechaModificacion: new Date().toISOString(),
          }),
        }
      );

      if (response.ok) {
        // Actualizar el movimiento en la lista
        const tipoSeleccionado = tiposTransaccion.find(
          (t) => t.id === parseInt(editingValue)
        );
        setMovimientos(
          movimientos.map((m) =>
            m.id === movimiento.id
              ? {
                  ...m,
                  idTipoTransaccion: editingValue,
                  descripcionTipoTransaccion: tipoSeleccionado?.descripcionTipoTransaccion || '',
                }
              : m
          )
        );
        setEditingId(null);
        setEditingValue('');
        Swal.fire({
          icon: 'success',
          title: 'Éxito',
          text: 'Tipo de transacción actualizado correctamente',
        });
      } else {
        throw new Error('Error al actualizar');
      }
    } catch (error) {
      console.error('Error al guardar:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Error al guardar los cambios',
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="right_col" role="main">
      <div className="x_content">
        <h2>Movimientos del Cajero</h2>
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                <TableCell><strong>ID</strong></TableCell>
                <TableCell><strong>Tipo de Transacción</strong></TableCell>
                <TableCell><strong>Concepto</strong></TableCell>
                <TableCell><strong>Número de Documento</strong></TableCell>
                <TableCell><strong>Monto</strong></TableCell>
                <TableCell><strong>Fecha</strong></TableCell>
                <TableCell align="center"><strong>Acciones</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {movimientos
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((movimiento) => (
                  <TableRow key={movimiento.id}>
                    <TableCell>{movimiento.id}</TableCell>
                    <TableCell>
                      {editingId === movimiento.id ? (
                        <Select
                          value={editingValue}
                          onChange={(e) => setEditingValue(e.target.value)}
                          size="small"
                          fullWidth
                        >
                          <MenuItem value="">Seleccionar...</MenuItem>
                          {tiposTransaccion.map((tipo) => (
                            <MenuItem key={tipo.id} value={String(tipo.id)}>
                              {tipo.descripcionTipoTransaccion}
                            </MenuItem>
                          ))}
                        </Select>
                      ) : (
                        movimiento.descripcionTipoTransaccion
                      )}
                    </TableCell>
                    <TableCell>{movimiento.concepto || '-'}</TableCell>
                    <TableCell>{movimiento.numeroDocumento || '-'}</TableCell>
                    <TableCell>
                      L. {parseFloat(movimiento.valor || 0).toFixed(2)}
                    </TableCell>
                    <TableCell>
                      {new Date(movimiento.fechaTransaccion).toLocaleString('es-HN')}
                    </TableCell>
                    <TableCell align="center">
                      {editingId === movimiento.id ? (
                        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                          <Button
                            size="small"
                            variant="contained"
                            color="success"
                            onClick={() => handleGuardarEdicion(movimiento)}
                            disabled={isSaving || !tienePermiso('Editar Movimiento Cajero')}
                          >
                            {isSaving ? <CircularProgress size={20} /> : 'Guardar'}
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            color="error"
                            onClick={handleCancelarEdicion}
                            disabled={isSaving}
                          >
                            Cancelar
                          </Button>
                        </Box>
                      ) : (
                        <Button
                          size="small"
                          variant="outlined"
                          color="primary"
                          onClick={() => handleEditarTipo(movimiento)}
                          disabled={!tienePermiso('Editar Movimiento Cajero')}
                        >
                          Editar
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={movimientos.length}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </div>
    </div>
  );
};

export default MovimientosCajero;
